/**
 * Dossier Templates — Section 5.5 of the architecture blueprint (wired 5.6).
 *
 * Two-pane editor: template list on the left (platform defaults + this
 * tenant's forks), the selected template on the right — header fields, slot
 * items (doc type / required / recency / order / conditional_on JSON), and
 * the fork-on-edit flow: platform rows are read-only for brokerage admins,
 * who fork a tenant-scoped copy instead (same template_key shadows the
 * default at dossier-creation time). Item saves are diffed server-side, so
 * removing a slot an open dossier uses returns a 409 with a hint.
 */

import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { adminService, ApiClientError } from "@re-platform/api-client";
import type {
  DocType,
  DossierTemplate,
  TemplateFlowType,
  TemplateItemInput,
} from "@re-platform/types";
import { Button, Card, StatusPill, Toast } from "@re-platform/ui";

import { api } from "../../lib/api";
import { ExpandSign } from "../../lib/cards";
import { useMediaQuery } from "../../lib/useMediaQuery";

interface ItemDraft {
  doc_type_key: string;
  is_required: boolean;
  recency_days: string;
  display_order: number;
  conditional_on: string;
}

function toDraft(template: DossierTemplate): ItemDraft[] {
  return template.items.map((item) => ({
    doc_type_key: item.doc_type_key,
    is_required: item.is_required,
    recency_days: item.recency_days?.toString() ?? "",
    display_order: item.display_order,
    conditional_on: item.conditional_on
      ? JSON.stringify(item.conditional_on)
      : "",
  }));
}

function toInputs(drafts: ItemDraft[]): TemplateItemInput[] {
  return drafts.map((draft, index) => ({
    doc_type_key: draft.doc_type_key,
    is_required: draft.is_required,
    recency_days: draft.recency_days ? Number(draft.recency_days) : null,
    display_order: index,
    conditional_on: draft.conditional_on
      ? (JSON.parse(draft.conditional_on) as Record<string, unknown>)
      : null,
  }));
}

export function Templates() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Below this there is no room for two panes side by side, so the list
  // becomes a disclosure above the editor and closes once you pick — the
  // editor is what you came for, and a full list pushing it off-screen is
  // the thing that made this unusable narrow.
  const narrow = useMediaQuery("(max-width: 860px)");
  const [listOpen, setListOpen] = useState(false);

  const templatesQuery = useQuery({
    queryKey: ["templates"],
    queryFn: () => adminService.listTemplates(api, { include_inactive: true }),
  });
  const docTypesQuery = useQuery({
    queryKey: ["doc-types"],
    queryFn: () => adminService.listDocTypes(api, { active: true }),
  });

  const templates = templatesQuery.data ?? [];
  const selected =
    templates.find((t) => t.id === selectedId) ?? templates[0] ?? null;

  async function run(work: () => Promise<unknown>, done: string) {
    setBusy(true);
    try {
      await work();
      setToast(done);
      await queryClient.invalidateQueries({ queryKey: ["templates"] });
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Request failed");
    } finally {
      setBusy(false);
    }
  }

  if (templatesQuery.isError) {
    const unauthorized =
      templatesQuery.error instanceof ApiClientError &&
      [401, 403].includes(templatesQuery.error.status);
    return (
      <>
        <h1 style={{ marginTop: 0 }}>Dossier templates</h1>
        <Card padding="lg">
          <p style={{ margin: 0 }}>
            {unauthorized
              ? "Not authorized — paste a super_admin / brokerage_admin session token in the sidebar."
              : `Couldn't load templates: ${(templatesQuery.error as Error).message}`}
          </p>
        </Card>
      </>
    );
  }

  return (
    <>
      <h1 style={{ marginTop: 0 }}>Dossier templates</h1>
      <p style={{ color: "var(--rp-color-text-muted)" }}>
        Platform defaults are the regulatory baseline — editing one as a
        brokerage forks it into a tenant-scoped copy that shadows the default.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: narrow ? "1fr" : "280px 1fr",
          gap: 16,
          alignItems: "start",
        }}
      >
        {/* list pane */}
        <Card>
          {narrow && (
            <button
              type="button"
              onClick={() => setListOpen((o) => !o)}
              aria-expanded={listOpen}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "6px 4px",
                border: "none",
                background: "none",
                font: "inherit",
                fontWeight: 700,
                color: "inherit",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <ExpandSign open={listOpen} />
              {selected ? selected.display_name : "Templates"}
              <span
                style={{
                  marginLeft: "auto",
                  fontWeight: 400,
                  fontSize: 12.5,
                  color: "var(--rp-color-text-muted)",
                }}
              >
                {templates.length} template{templates.length === 1 ? "" : "s"}
              </span>
            </button>
          )}
          {templatesQuery.isPending && <p style={{ margin: 8 }}>Loading…</p>}
          {(!narrow || listOpen) &&
            templates.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => {
                  setSelectedId(template.id);
                  setListOpen(false);
                }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 12px",
                  border: "none",
                  borderRadius: "var(--rp-radius-md)",
                  background:
                    selected?.id === template.id
                      ? "var(--rp-color-primary-soft)"
                      : "transparent",
                  cursor: "pointer",
                  font: "inherit",
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {template.display_name}
                  {!template.active && " (inactive)"}
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                  <StatusPill
                    tone={
                      template.flow_type === "MORTGAGE" ? "info" : "neutral"
                    }
                  >
                    {template.flow_type.toLowerCase()}
                  </StatusPill>
                  <StatusPill
                    tone={template.scope === "platform" ? "neutral" : "success"}
                  >
                    {template.scope}
                  </StatusPill>
                </div>
              </button>
            ))}
          {(!narrow || listOpen) && (
            <div style={{ padding: 8 }}>
              <NewTemplate
                busy={busy}
                docTypes={docTypesQuery.data ?? []}
                onCreate={(body) =>
                  void run(
                    () => adminService.createTemplate(api, body),
                    `Template "${body.display_name}" created.`,
                  )
                }
              />
            </div>
          )}
        </Card>

        {/* editor pane */}
        {selected ? (
          <TemplateEditor
            key={selected.id}
            template={selected}
            docTypes={docTypesQuery.data ?? []}
            busy={busy}
            onPatch={(body) =>
              void run(
                () => adminService.updateTemplate(api, selected.id, body),
                "Saved.",
              )
            }
            onSaveItems={(items) =>
              void run(
                () =>
                  adminService.replaceTemplateItems(api, selected.id, items),
                "Slots saved.",
              )
            }
            onFork={() =>
              void run(
                () => adminService.forkTemplate(api, selected.id),
                "Forked into a tenant copy.",
              )
            }
            onDelete={() =>
              void run(
                () => adminService.deleteTemplate(api, selected.id),
                "Template deleted.",
              )
            }
          />
        ) : (
          !templatesQuery.isPending && (
            <Card padding="lg">
              <p style={{ margin: 0, color: "var(--rp-color-text-muted)" }}>
                No templates yet — create one on the left.
              </p>
            </Card>
          )
        )}
      </div>

      <Toast message={toast} onDismiss={() => setToast(null)} />
    </>
  );
}

function TemplateEditor({
  template,
  docTypes,
  busy,
  onPatch,
  onSaveItems,
  onFork,
  onDelete,
}: {
  template: DossierTemplate;
  docTypes: readonly DocType[];
  busy: boolean;
  onPatch: (body: {
    display_name?: string;
    description?: string;
    active?: boolean;
  }) => void;
  onSaveItems: (items: TemplateItemInput[]) => void;
  onFork: () => void;
  onDelete: () => void;
}) {
  const [displayName, setDisplayName] = useState(template.display_name);
  const [description, setDescription] = useState(template.description ?? "");
  const [items, setItems] = useState<ItemDraft[]>(() => toDraft(template));
  const [itemsError, setItemsError] = useState<string | null>(null);
  // The editor pane is narrower than the page, so it crosses over sooner than
  // the two-pane breakpoint does.
  const narrow = useMediaQuery("(max-width: 1000px)");
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  // A row holds a select and three text boxes. Arming the drag only when the
  // press did NOT land on a form control keeps click-and-drag inside an input
  // doing what it always does — selecting text — instead of picking the row up.
  const [dragArmed, setDragArmed] = useState(false);

  // Re-sync local drafts when a save lands (items get fresh ids/orders).
  useEffect(() => {
    setDisplayName(template.display_name);
    setDescription(template.description ?? "");
    setItems(toDraft(template));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.updated_at, template.items.length]);

  function saveItems() {
    try {
      const inputs = toInputs(items);
      setItemsError(null);
      onSaveItems(inputs);
    } catch {
      setItemsError("One of the conditional_on fields isn't valid JSON.");
    }
  }

  function update(index: number, patch: Partial<ItemDraft>) {
    setItems((current) =>
      current.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  }

  function move(index: number, delta: -1 | 1) {
    setItems((current) => {
      const next = [...current];
      const target = index + delta;
      if (target < 0 || target >= next.length) return current;
      const [row] = next.splice(index, 1);
      next.splice(target, 0, row!);
      return next;
    });
  }

  /** Drag a slot to a new position. */
  function reorder(from: number, to: number) {
    if (from === to) return;
    setItems((current) => {
      const next = [...current];
      const [row] = next.splice(from, 1);
      next.splice(to, 0, row!);
      return next;
    });
  }

  return (
    <Card padding="lg">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18 }}>{template.display_name}</h2>
        <StatusPill
          tone={template.scope === "platform" ? "neutral" : "success"}
        >
          {template.scope}
        </StatusPill>
        <code style={{ fontSize: 12, color: "var(--rp-color-text-muted)" }}>
          {template.template_key}
        </code>
        <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {template.scope === "platform" && (
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={onFork}
            >
              Fork for my brokerage
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => onPatch({ active: !template.active })}
          >
            {template.active ? "Deactivate" : "Activate"}
          </Button>
          <Button variant="danger" size="sm" disabled={busy} onClick={onDelete}>
            Delete
          </Button>
        </span>
      </div>

      {/* header fields */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 12,
          marginTop: 16,
        }}
      >
        <label style={label()}>
          Display name
          <input
            style={input()}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>
        <label style={label()}>
          Description
          <input
            style={input()}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
      </div>
      <Button
        size="sm"
        disabled={
          busy ||
          (displayName === template.display_name &&
            description === (template.description ?? ""))
        }
        onClick={() => onPatch({ display_name: displayName, description })}
        style={{ marginTop: 10 }}
      >
        Save header
      </Button>

      {/* The slots ARE the template — the column header names them, and the
          buttons below say "Add slot" / "Save slots", so a heading saying it a
          third time earned nothing but vertical space. */}
      {/* Slot rows, not a table: a table cannot reflow, and this one carried a
          select, a checkbox, a number and a JSON string per row — it ran off
          the side of the editor pane long before the page itself got narrow.
          Each slot is its own bordered row whose fields wrap; the shared
          column header only appears when there is width to align to, and
          below that each field carries its own label instead. */}
      {!narrow && items.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 10,
            marginTop: 24,
            padding: "0 10px 6px",
            fontSize: 11,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: "var(--rp-color-text-muted)",
            borderBottom: "1px solid var(--rp-color-border)",
            marginBottom: 8,
          }}
        >
          {/* Matches the grip's footprint so the labels line up with the
              fields rather than sitting one column to the left. */}
          <span style={{ flex: "0 0 20px" }} />
          <span style={{ flex: "2 1 200px" }}>Document type</span>
          <span style={{ flex: "0 0 80px" }}>Required</span>
          <span style={{ flex: "0 0 150px" }}>Recency</span>
          <span style={{ flex: "2 1 220px" }}>Conditional on</span>
          <span style={{ flex: "0 0 44px" }} />
        </div>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          // The column header owns the gap above whenever it is showing.
          marginTop: !narrow && items.length > 0 ? 0 : 24,
        }}
      >
        {items.map((item, index) => (
          <div
            key={`${item.doc_type_key}-${index}`}
            draggable={dragArmed}
            onMouseDown={(e) => {
              const tag = (e.target as HTMLElement).tagName;
              setDragArmed(
                !["INPUT", "SELECT", "OPTION", "TEXTAREA"].includes(tag),
              );
            }}
            onDragStart={(e) => {
              setDragFrom(index);
              e.dataTransfer.effectAllowed = "move";
              // Firefox refuses to start a drag without payload.
              e.dataTransfer.setData("text/plain", String(index));
            }}
            onDragOver={(e) => {
              if (dragFrom === null) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              setDragOver(index);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragFrom !== null) reorder(dragFrom, index);
              setDragFrom(null);
              setDragOver(null);
            }}
            onDragEnd={() => {
              setDragFrom(null);
              setDragOver(null);
              setDragArmed(false);
            }}
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: narrow ? "stretch" : "center",
              gap: 10,
              padding: 10,
              border: "1px solid var(--rp-color-border)",
              borderRadius: "var(--rp-radius-md)",
              background: "var(--rp-color-surface)",
              opacity: dragFrom === index ? 0.4 : 1,
              // Where it would land, drawn on the row being hovered.
              boxShadow:
                dragOver === index && dragFrom !== null && dragFrom !== index
                  ? "0 -2px 0 0 var(--rp-color-accent, var(--rp-color-text))"
                  : undefined,
            }}
          >
            {/* Grip: the drag affordance, and the keyboard path to the same
                action now that the arrow buttons are gone. */}
            <button
              type="button"
              aria-label={`Reorder ${item.doc_type_key}. Use arrow up and arrow down.`}
              onKeyDown={(e) => {
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  move(index, -1);
                } else if (e.key === "ArrowDown") {
                  e.preventDefault();
                  move(index, 1);
                }
              }}
              style={{
                flex: "0 0 auto",
                alignSelf: narrow ? "flex-start" : "center",
                border: "none",
                background: "none",
                padding: "2px 2px",
                color: "var(--rp-color-text-muted)",
                cursor: "grab",
                lineHeight: 1,
                fontSize: 15,
              }}
            >
              ⠿
            </button>
            <label style={{ flex: "2 1 200px", minWidth: 0 }}>
              {narrow && <span style={slotLabel()}>Document type</span>}
              <select
                style={{ ...input(), marginTop: 0 }}
                value={item.doc_type_key}
                onChange={(e) =>
                  update(index, { doc_type_key: e.target.value })
                }
              >
                {docTypes.map((dt) => (
                  <option key={dt.id} value={dt.doc_type_key}>
                    {dt.display_name}
                  </option>
                ))}
                {!docTypes.some(
                  (dt) => dt.doc_type_key === item.doc_type_key,
                ) && (
                  <option value={item.doc_type_key}>{item.doc_type_key}</option>
                )}
              </select>
            </label>

            <label
              style={{
                flex: narrow ? "1 1 100%" : "0 0 80px",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
              }}
            >
              <input
                type="checkbox"
                checked={item.is_required}
                onChange={(e) =>
                  update(index, { is_required: e.target.checked })
                }
              />
              {/* The header says "Required" when there is one; without it the
                  checkbox would be an unlabelled box. */}
              {narrow && <span>Required</span>}
            </label>

            <label style={{ flex: "0 0 150px" }}>
              {narrow && <span style={slotLabel()}>Recency</span>}
              <span
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <input
                  style={{ ...input(), width: 72, marginTop: 0 }}
                  value={item.recency_days}
                  placeholder="any"
                  onChange={(e) =>
                    update(index, {
                      recency_days: e.target.value.replace(/\D/g, ""),
                    })
                  }
                />
                <span
                  style={{
                    fontSize: 12.5,
                    color: "var(--rp-color-text-muted)",
                  }}
                >
                  {item.recency_days ? "days" : "any age"}
                </span>
              </span>
            </label>

            <label style={{ flex: "2 1 220px", minWidth: 0 }}>
              {narrow && <span style={slotLabel()}>Conditional on (JSON)</span>}
              <input
                style={{
                  ...input(),
                  marginTop: 0,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 12.5,
                }}
                value={item.conditional_on}
                placeholder='{"field":"client.employment_status","equals":"self_employed"}'
                onChange={(e) =>
                  update(index, { conditional_on: e.target.value })
                }
              />
            </label>

            <span
              style={{
                flex: narrow ? "1 1 100%" : "0 0 44px",
                display: "flex",
                justifyContent: narrow ? "flex-end" : "flex-start",
                whiteSpace: "nowrap",
              }}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setItems((current) => current.filter((_, i) => i !== index))
                }
              >
                ✕
              </Button>
            </span>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <Button
          variant="secondary"
          size="sm"
          disabled={docTypes.length === 0}
          onClick={() =>
            setItems((current) => [
              ...current,
              {
                doc_type_key: docTypes[0]?.doc_type_key ?? "",
                is_required: true,
                recency_days: "",
                display_order: current.length,
                conditional_on: "",
              },
            ])
          }
        >
          Add slot
        </Button>
        <Button
          size="sm"
          disabled={busy || items.length === 0}
          onClick={saveItems}
        >
          Save slots
        </Button>
      </div>
      {itemsError && (
        <p
          style={{
            color: "var(--rp-color-danger)",
            fontSize: 13,
            marginTop: 8,
          }}
        >
          {itemsError}
        </p>
      )}
      {template.scope === "platform" && (
        <p
          style={{
            color: "var(--rp-color-text-muted)",
            fontSize: 12.5,
            marginTop: 12,
          }}
        >
          Heads-up: as a brokerage admin you can't edit a platform default — use
          "Fork for my brokerage" and edit the copy.
        </p>
      )}
    </Card>
  );
}

function NewTemplate({
  busy,
  docTypes,
  onCreate,
}: {
  busy: boolean;
  docTypes: readonly DocType[];
  onCreate: (body: {
    template_key: string;
    flow_type: TemplateFlowType;
    display_name: string;
    items: TemplateItemInput[];
  }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [flow, setFlow] = useState<TemplateFlowType>("MORTGAGE");
  if (!open) {
    return (
      <Button
        variant="secondary"
        size="sm"
        style={{ width: "100%" }}
        onClick={() => setOpen(true)}
      >
        New template…
      </Button>
    );
  }
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <input
        style={input()}
        placeholder="template_key (snake_case)"
        value={key}
        onChange={(e) => setKey(e.target.value)}
      />
      <input
        style={input()}
        placeholder="Display name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <select
        style={input()}
        value={flow}
        onChange={(e) => setFlow(e.target.value as TemplateFlowType)}
      >
        <option value="MORTGAGE">MORTGAGE</option>
        <option value="REAL_ESTATE">REAL_ESTATE</option>
      </select>
      <div style={{ display: "flex", gap: 6 }}>
        <Button
          size="sm"
          disabled={busy || !key || !name || docTypes.length === 0}
          onClick={() => {
            onCreate({
              template_key: key,
              flow_type: flow,
              display_name: name,
              items: [{ doc_type_key: docTypes[0]!.doc_type_key }],
            });
            setOpen(false);
            setKey("");
            setName("");
          }}
        >
          Create
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function label(): React.CSSProperties {
  return { display: "block", fontSize: 13, fontWeight: 600 };
}

function input(): React.CSSProperties {
  return {
    display: "block",
    width: "100%",
    marginTop: 4,
    padding: "7px 9px",
    border: "1px solid var(--rp-color-border)",
    borderRadius: "var(--rp-radius-sm)",
    font: "inherit",
    fontSize: 13.5,
    // Was a hardcoded "white" — invisible text on a dark background.
    background: "var(--rp-color-surface)",
    color: "var(--rp-color-text)",
  };
}

/** The per-field caption shown only when the shared column header is gone. */
function slotLabel(): React.CSSProperties {
  return {
    display: "block",
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--rp-color-text-muted)",
    marginBottom: 4,
  };
}
